import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Flex, Heading, LoadingOrbit, Text, Select, Warning, CheckCircle, useToast, TagBadge, type SelectOption } from '@chatsdet/ui';
import { API_URL } from '../../../config.js';
import { parseApiJson } from '../../../lib/api-error';
import { BRAND } from '../../../brand';
import { useLinearTeams } from '../../../hooks/useLinearIntegration';
import { useTicketingProvider } from '../../../hooks/useTicketingProviders';
import { IntegrationCapabilities } from './IntegrationCapabilities';

type LinearProject = { id: string; name: string };

type LinearSettings = {
  defaultTeamId: string | null;
  defaultProjectId: string | null;
  defaultLabelIds: string[] | null;
  webhookConfigured: boolean;
  enabledCapabilities: Record<string, boolean> | null;
};

type LinearSettingsSectionProps = {
  linearQuery: UseQueryResult<{ ok: boolean; details?: unknown }>;
  disconnectLinearMutation: UseMutationResult<unknown, Error, void>;
};

export function LinearSettingsSection({
  linearQuery,
  disconnectLinearMutation,
}: LinearSettingsSectionProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const name = BRAND.name;
  const { provider: linearProvider } = useTicketingProvider('linear');

  const settingsQuery = useQuery({
    queryKey: ['linear-settings'],
    queryFn: async (): Promise<{ ok: boolean; data?: LinearSettings }> => {
      const res = await fetch(`${API_URL}/integrations/linear/settings`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: LinearSettings }>(
        res,
        'Failed to fetch Linear settings'
      );
    },
    enabled: !!linearQuery.data?.ok,
  });

  const teamsQuery = useLinearTeams(!!linearQuery.data?.ok);

  const [selectedTeamId, setSelectedTeamId] = React.useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);

  // Sync from settings once loaded
  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setSelectedTeamId(settingsQuery.data.data.defaultTeamId);
      setSelectedProjectId(settingsQuery.data.data.defaultProjectId);
    }
  }, [settingsQuery.data]);

  const projectsQuery = useQuery({
    queryKey: ['linear-projects', selectedTeamId],
    queryFn: async (): Promise<{ ok: boolean; data?: LinearProject[] }> => {
      const res = await fetch(
        `${API_URL}/integrations/linear/projects?teamId=${selectedTeamId}`,
        { credentials: 'include' }
      );
      return parseApiJson<{ ok: boolean; data?: LinearProject[] }>(
        res,
        'Failed to fetch Linear projects'
      );
    },
    enabled: !!selectedTeamId,
  });

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/linear/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          defaultTeamId: selectedTeamId,
          defaultProjectId: selectedProjectId,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['linear-settings'] });
      setDirty(false);
      toast.success('Linear settings saved');
    },
  });

  const saveCapabilitiesMutation = useMutation({
    mutationFn: async (enabledCapabilities: Record<string, boolean>) => {
      const res = await fetch(`${API_URL}/integrations/linear/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabledCapabilities }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['linear-settings'] });
      toast.success('Feature preferences saved');
    },
  });

  const teamOptions = React.useMemo<SelectOption[]>(
    () =>
      (teamsQuery.data?.data ?? []).map((t) => ({
        id: t.id,
        label: `${t.name} (${t.key})`,
      })),
    [teamsQuery.data]
  );

  const projectOptions = React.useMemo<SelectOption[]>(
    () =>
      (projectsQuery.data?.data ?? []).map((p) => ({
        id: p.id,
        label: p.name,
      })),
    [projectsQuery.data]
  );

  if (linearQuery.isLoading) {
    return (
      <Box className="space-y-6">
        <Flex align="center" justify="center" className="py-8">
          <LoadingOrbit size="md" />
        </Flex>
      </Box>
    );
  }

  if (!linearQuery.data?.ok) {
    return (
      <Box className="space-y-6">
        <Card padding="lg">
          <CardHeader>
            <Flex align="start" gap="md">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Warning size={24} className="text-warning" />
              </div>
              <div className="flex-1">
                <Flex align="center" gap="sm">
                  <Heading level={3}>Linear Integration</Heading>
                  <TagBadge name="Not Connected" color="yellow" hideDot />
                </Flex>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Connect your Linear account to create tickets from issues and sync status
                </Text>
              </div>
            </Flex>
          </CardHeader>
          <CardContent>
            <Text size="sm" className="text-muted-foreground mb-4">
              Securely connect via OAuth to create Linear tickets from {name} issues and
              auto-close issues when tickets are resolved
            </Text>
            <Button
              variant="primary"
              onPress={() => {
                window.location.href = `${API_URL}/integrations/linear/oauth/start`;
              }}
            >
              Connect with OAuth
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="space-y-6">
      {/* Connection status */}
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="p-2 bg-success/10 rounded-lg">
              <CheckCircle size={24} className="text-success" />
            </div>
            <div className="flex-1">
              <Flex align="center" gap="sm">
                <Heading level={3}>Linear Integration</Heading>
                <TagBadge name="Connected" color="blue" hideDot />
                {settingsQuery.data?.data?.webhookConfigured && (
                  <TagBadge name="Webhooks Active" color="blue" hideDot />
                )}
              </Flex>
              <Text size="sm" className="text-muted-foreground mt-1">
                Issues can be linked to Linear tickets. Status changes sync automatically.
              </Text>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onPress={() => {
                if (window.confirm('Are you sure you want to disconnect Linear?')) {
                  disconnectLinearMutation.mutate();
                }
              }}
              isDisabled={disconnectLinearMutation.isPending}
            >
              {disconnectLinearMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </Flex>
        </CardHeader>
      </Card>

      {/* Default settings */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Default Settings</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Configure defaults for Linear tickets created from {name} issues
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="lg">
            <div>
              <Text size="sm" className="font-medium mb-1.5">
                Default Team
              </Text>
              {teamsQuery.isLoading ? (
                <Text size="sm" className="text-muted-foreground">Loading teams...</Text>
              ) : (
                <Select
                  aria-label="Default Linear team"
                  selectedKey={selectedTeamId}
                  items={teamOptions}
                  placeholder="Select a team..."
                  onSelectionChange={(key) => {
                    setSelectedTeamId(key as string);
                    setSelectedProjectId(null);
                    setDirty(true);
                  }}
                />
              )}
            </div>

            {selectedTeamId && (
              <div>
                <Text size="sm" className="font-medium mb-1.5">
                  Default Project (optional)
                </Text>
                {projectsQuery.isLoading ? (
                  <Text size="sm" className="text-muted-foreground">Loading projects...</Text>
                ) : (
                  <Select
                    aria-label="Default Linear project"
                    selectedKey={selectedProjectId}
                    items={projectOptions}
                    placeholder="No default project"
                    onSelectionChange={(key) => {
                      setSelectedProjectId(key as string);
                      setDirty(true);
                    }}
                  />
                )}
              </div>
            )}

            <Flex gap="sm">
              <Button
                variant="primary"
                size="sm"
                onPress={() => saveSettingsMutation.mutate()}
                isDisabled={!dirty || saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      {linearProvider && (
        <IntegrationCapabilities
          capabilities={linearProvider.capabilities}
          labels={linearProvider.capabilityLabels}
          enabledCapabilities={settingsQuery.data?.data?.enabledCapabilities ?? null}
          onSave={(prefs) => saveCapabilitiesMutation.mutate(prefs)}
          isSaving={saveCapabilitiesMutation.isPending}
        />
      )}
    </Box>
  );
}
